import { ONE_DAY_IN_SECONDS } from '@/constants';
import {
  Account,
  Breed,
  Contact,
  Donor,
  Drug,
  GET_ACCOUNT_BY_DATE_AND_SITE,
  GET_ASPIRATION_SITES,
  GET_BREEDS,
  GET_FERTILIZATION_SITES,
  GET_OPTION_SETS_BY_NAME,
  GET_PENS,
  GET_SITES,
  GET_SITES_SEARCH,
  GET_STORAGE_LOCATIONS,
  GET_YARDS,
  Location,
  LocationFilterInput,
  OptionSet,
  Pen,
  ScheduleMaster,
  SEARCH_ACCOUNTS_AUTOCOMPLETE,
  SEARCH_ALL_DONORS_AUTOCOMPLETE,
  SEARCH_ASPIRATION_SITES,
  SEARCH_CONTACTS_AUTOCOMPLETE,
  SEARCH_DONORS_BY_OWNER_ID_AUTOCOMPLETE,
  SEARCH_DRUGS,
  SEARCH_SCHEDULE_MASTER,
  SEARCH_SEMEN,
  SEARCH_SEMEN_LOOKUP,
  SEARCH_SHIPMENT_GROUPS,
  SEARCH_SHIPMENTS_AUTOCOMPLETE,
  SEARCH_SIRES,
  SEARCH_SIRES_LOOKUP,
  SEARCH_SYSTEM_USERS_AUTOCOMPLETE,
  Semen,
  Shipment,
  ShipmentGroup,
  Sire,
  Site,
  StorageLocation,
  STRING_MAP_REQUEST,
  StringMap,
  SystemUser,
  Yard,
} from '@/rtk-query.gql';

import { enhancedApi } from '../api/baseApi';

interface AspirationSite {
  id: string;
  name: string;
  fertilizationSiteId: string;
  fertilizationSite: {
    id: string;
    name: string;
  };
}

interface AspirationSitesResponse {
  aspirationSites: AspirationSite[];
}

interface LocationResponse {
  locations: {
    items: Location[];
  };
}

interface BreedsResponse {
  breeds: {
    items: Breed[];
  };
}

interface YardsResponse {
  yards: {
    items: Yard[];
  };
}

interface FertilizationSitesResponse {
  sites: {
    items: Site[];
  };
}

interface OptionSetsResponse {
  optionSets: {
    items: OptionSet[];
  };
}

interface PensResponse {
  pens: {
    items: Pen[];
  };
}

interface SitesResponse {
  sites: {
    items: Site[];
  };
}

interface SearchDonorsResponse {
  donors: {
    items: Donor[];
  };
}

interface SearchDrugsResponse {
  drugs: {
    items: Drug[];
  };
}

interface StringMapResponse {
  stringMaps: {
    items: StringMap[];
  };
}

interface SearchAccountsResponse {
  accounts: {
    items: Account[];
  };
}

interface SearchSchedulesResponse {
  scheduleMaster: {
    items: ScheduleMaster[];
  };
}

interface SearchSemenResponse {
  semen: {
    items: Semen[];
  };
}

interface GetStorageLocationsResponse {
  storageLocations: {
    items: StorageLocation[];
  };
}

interface SearchShipmentGroupsResponse {
  shipmentGroups: {
    items: ShipmentGroup[];
  };
}

interface SearchShipmentsResponse {
  shipments: {
    items: Shipment[];
  };
}

interface SearchSiresResponse {
  sires: {
    items: Sire[];
  };
}

interface SearchSystemUsersResponse {
  results: {
    items: SystemUser[];
  };
}

interface SearchContactsResponse {
  contacts: {
    items: Contact[];
  };
}

export const lookupsApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getAspirationSites: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: GET_ASPIRATION_SITES,
        variables,
      }),
      transformResponse: (response: AspirationSitesResponse) =>
        response.aspirationSites,
    }),
    searchAspirationSites: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { where: LocationFilterInput }) => ({
        document: SEARCH_ASPIRATION_SITES,
        variables,
      }),
      transformResponse: (response: LocationResponse) =>
        response.locations.items,
    }),
    getBreeds: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: () => ({
        document: GET_BREEDS,
      }),
      transformResponse: (response: BreedsResponse) => response.breeds.items,
    }),
    getYards: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: () => ({
        document: GET_YARDS,
      }),
      transformResponse: (response: YardsResponse) => response.yards.items,
    }),
    getFertilizationSites: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: () => ({
        document: GET_FERTILIZATION_SITES,
      }),
      transformResponse: (response: FertilizationSitesResponse) =>
        response.sites.items,
    }),
    searchAllDonors: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: ({ search, selectionLimit }) => ({
        document: SEARCH_ALL_DONORS_AUTOCOMPLETE,
        variables: { search, selectionLimit },
      }),
      transformResponse: (response: SearchDonorsResponse) =>
        response.donors.items,
    }),
    searchDonorsByOwnerId: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: ({ search, selectionLimit, ownerId }) => ({
        document: SEARCH_DONORS_BY_OWNER_ID_AUTOCOMPLETE,
        variables: { search, selectionLimit, ownerId },
      }),
      transformResponse: (response: SearchDonorsResponse) =>
        response.donors.items,
    }),
    searchDrugs: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_DRUGS,
        variables,
      }),
      transformResponse: (response: SearchDrugsResponse) =>
        response.drugs.items,
    }),
    getPens: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: () => ({
        document: GET_PENS,
      }),
      transformResponse: (response: PensResponse) => response.pens.items,
    }),
    getSites: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: GET_SITES,
        variables,
      }),
      transformResponse: (response: SitesResponse) => response.sites.items,
    }),
    getSitesSearch: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: GET_SITES_SEARCH,
        variables,
      }),
      transformResponse: (response: SitesResponse) => response.sites.items,
    }),
    getOptionSetsByName: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (name: string) => ({
        document: GET_OPTION_SETS_BY_NAME,
        variables: {
          name,
        },
      }),
      transformResponse: (response: OptionSetsResponse) =>
        response.optionSets.items,
    }),
    getStringMaps: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: {
        objectTypeCode: number;
        attributeName: string;
      }) => ({
        document: STRING_MAP_REQUEST,
        variables,
      }),
      transformResponse: (response: StringMapResponse) =>
        response.stringMaps.items,
    }),
    searchAccounts: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: SEARCH_ACCOUNTS_AUTOCOMPLETE,
        variables,
      }),
      transformResponse: (response: SearchAccountsResponse) =>
        response.accounts.items,
    }),
    getFreshStrawAccounts: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: GET_ACCOUNT_BY_DATE_AND_SITE,
        variables,
      }),
    }),
    searchSchedules: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_SCHEDULE_MASTER,
        variables,
      }),
      transformResponse: (response: SearchSchedulesResponse) =>
        response.scheduleMaster.items,
    }),
    searchSemen: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: SEARCH_SEMEN,
        variables,
      }),
      transformResponse: (response: SearchSemenResponse) =>
        response.semen.items,
    }),
    searchSemenLookup: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: SEARCH_SEMEN_LOOKUP,
        variables,
      }),
    }),

    searchStorageLocations: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables) => ({
        document: GET_STORAGE_LOCATIONS,
        variables,
      }),
      transformResponse: (response: GetStorageLocationsResponse) =>
        response.storageLocations.items,
    }),
    searchShipmentGroups: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_SHIPMENT_GROUPS,
        variables,
      }),
      transformResponse: (response: SearchShipmentGroupsResponse) =>
        response.shipmentGroups.items,
    }),
    searchShipments: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_SHIPMENTS_AUTOCOMPLETE,
        variables,
      }),
      transformResponse: (response: SearchShipmentsResponse) =>
        response.shipments.items,
    }),
    searchSiresLookup: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_SIRES_LOOKUP,
        variables,
      }),
      transformResponse: (response: SearchSiresResponse) =>
        response.sires.items,
    }),
    searchSires: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_SIRES,
        variables,
      }),
      providesTags: ['Sires'],
    }),
    searchSystemUsers: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_SYSTEM_USERS_AUTOCOMPLETE,
        variables,
      }),
      transformResponse: (response: SearchSystemUsersResponse) =>
        response.results.items,
    }),
    searchContacts: build.query({
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      query: (variables: { search: string; selectionLimit: number }) => ({
        document: SEARCH_CONTACTS_AUTOCOMPLETE,
        variables,
      }),
      transformResponse: (response: SearchContactsResponse) =>
        response.contacts.items,
    }),
  }),
});

export const {
  useGetAspirationSitesQuery,
  useGetBreedsQuery,
  useGetFertilizationSitesQuery,
  useGetOptionSetsByNameQuery,
  useGetPensQuery,
  useSearchAspirationSitesQuery,
  useGetSitesQuery,
  useLazyGetSitesQuery,
  useLazyGetSitesSearchQuery,
  useGetSitesSearchQuery,
  useGetStringMapsQuery,
  useLazyGetAspirationSitesQuery,
  useLazyGetBreedsQuery,
  useGetYardsQuery,
  useLazyGetYardsQuery,
  useLazyGetFertilizationSitesQuery,
  useLazyGetOptionSetsByNameQuery,
  useLazyGetPensQuery,
  useSearchSemenLookupQuery,
  useLazySearchSemenLookupQuery,
  useLazyGetStringMapsQuery,
  useLazySearchAccountsQuery,
  useLazySearchAllDonorsQuery,
  useLazySearchAspirationSitesQuery,
  useLazySearchDonorsByOwnerIdQuery,
  useLazySearchDrugsQuery,
  useLazySearchSchedulesQuery,
  useLazySearchSemenQuery,
  useLazySearchStorageLocationsQuery,
  useLazySearchShipmentGroupsQuery,
  useLazySearchShipmentsQuery,
  useLazySearchSiresLookupQuery,
  useLazySearchSiresQuery,
  useSearchSiresQuery,
  useLazySearchSystemUsersQuery,
  useLazySearchContactsQuery,
  useSearchAccountsQuery,
  useSearchAllDonorsQuery,
  useSearchDonorsByOwnerIdQuery,
  useSearchDrugsQuery,
  useSearchSchedulesQuery,
  useSearchSemenQuery,
  useSearchStorageLocationsQuery,
  useSearchSiresLookupQuery,
  useSearchSystemUsersQuery,
  useSearchContactsQuery,
  useGetFreshStrawAccountsQuery,
} = lookupsApi;
